import { useT } from '@transifex/react';
import { useCallback, useMemo, useState } from 'react';

import { OTHER_CHOICE_VALUE } from '../types/assessmentQuestion';
import {
  EMPTY_QUESTION_FIELD_VALUES,
  type QuestionFieldHandlers,
  type QuestionFieldState,
  type QuestionFieldValues,
} from '../types/questionField';
import {
  checkDateAnswer,
  checkNumberAnswer,
  checkPhoneAnswer,
  type QuestionErrorPatch,
  type QuestionPendingState,
} from '../utils/evaluateQuestionAnswer';
import { isExclusiveChoiceValue } from '../utils/questionFlags';
import type { ActiveQuestion } from '../utils/resolveQuestionChoices';

import { useRepeatingListRows } from './useRepeatingListRows';

export type AssessmentPendingStateApi = {
  /** Everything one question's renderer draws from. */
  fieldState: (question: ActiveQuestion) => QuestionFieldState;
  /**
   * That question's callbacks, wired to this store. `inlineAddRow` adds the
   * in-place "+ Add another" control a grouped page needs (see
   * `QuestionFieldHandlers.onAddRowInline`).
   */
  handlers: (
    question: ActiveQuestion,
    options?: { inlineAddRow?: boolean }
  ) => QuestionFieldHandlers;
  /** The question's pending answer, in the shape the pure evaluator takes. */
  pendingAnswer: (question: ActiveQuestion) => QuestionPendingState;
  /** Applies the error writes an evaluation returned. */
  applyErrorPatch: (key: string, patch: QuestionErrorPatch) => void;
  /** Starting rows for a repeatingList question; idempotent per key. */
  seedRows: (question: ActiveQuestion) => void;
  /** Appends a row — the footer's "+ Add another" in single-question mode. */
  addRow: (question: ActiveQuestion) => void;
};

/**
 * Owns every question's pending (not yet committed) answer and its own error
 * copy, keyed BY QUESTION KEY rather than held as a single value for "the
 * active question" — not because today's one-question-per-screen walk needs it
 * (only one key is ever live at a time there), but so the exact same state
 * serves a `singleScreen` node, where every question of the node is on screen
 * at once. A question's slot simply starts absent and is never written until
 * the member interacts with THAT question, so moving on to a new key needs no
 * explicit reset of the old one.
 *
 * Exposes intent (`toggleChoice`, `onTextChange`, `applyErrorPatch`), not raw
 * setters, so the rules about what a given interaction clears live here rather
 * than being restated by every layout that renders a question.
 */
export function useAssessmentPendingState(): AssessmentPendingStateApi {
  const t = useT();
  const [valuesByKey, setValuesByKey] = useState<Record<string, QuestionFieldValues>>({});

  const getValues = useCallback(
    (key: string): QuestionFieldValues => valuesByKey[key] ?? EMPTY_QUESTION_FIELD_VALUES,
    [valuesByKey]
  );

  /** Merges `changes` into one question's slot, creating it on first write. */
  const patchValues = useCallback((key: string, changes: Partial<QuestionFieldValues>) => {
    setValuesByKey((prev) => ({
      ...prev,
      [key]: { ...(prev[key] ?? EMPTY_QUESTION_FIELD_VALUES), ...changes },
    }));
  }, []);

  const clearRequiredError = useCallback(
    (key: string) => patchValues(key, { showRequiredError: false }),
    [patchValues]
  );

  const rows = useRepeatingListRows({ onRowEdited: clearRequiredError });

  // Toggle a choice: multi-select adds/removes it from the set; single-select
  // replaces the whole selection with just this value. Within multi-select,
  // an `exclusive` choice (e.g. "None of these conditions") enforces mutual
  // exclusion in both directions: selecting it drops every other selection,
  // and selecting anything else drops a previously-selected exclusive one.
  // Also clears this question's inline Other text the moment Other is no
  // longer part of the selection — folded in here rather than a separate
  // effect watching the selection, since this is the only place it ever
  // changes.
  const toggleChoice = useCallback(
    (question: ActiveQuestion, value: string) => {
      const key = question.key;
      const isMultiSelect = question.isMultiSelect === true;
      const current = getValues(key).selected;
      let next: string[];
      if (!isMultiSelect) {
        next = [value];
      } else if (isExclusiveChoiceValue(question, value)) {
        next = current.includes(value) ? [] : [value];
      } else if (current.includes(value)) {
        next = current.filter((v) => v !== value);
      } else {
        next = [...current.filter((v) => !isExclusiveChoiceValue(question, v)), value];
      }
      patchValues(key, {
        selected: next,
        showRequiredError: false,
        ...(next.includes(OTHER_CHOICE_VALUE) ? {} : { otherInput: '' }),
      });
    },
    [getValues, patchValues]
  );

  const fieldState = useCallback(
    (question: ActiveQuestion): QuestionFieldState => ({
      ...getValues(question.key),
      rows: rows.getRows(question.key),
      statusMessage: rows.getStatusMessage(question.key),
    }),
    [getValues, rows]
  );

  const pendingAnswer = useCallback(
    (question: ActiveQuestion): QuestionPendingState => {
      const { selected, dateValue, textValue, otherInput } = getValues(question.key);
      return { selected, dateValue, textValue, otherInput, rows: rows.getRows(question.key) };
    },
    [getValues, rows]
  );

  const applyErrorPatch = useCallback(
    (key: string, patch: QuestionErrorPatch) => {
      // An ABSENT field is left untouched (see `QuestionErrorPatch`), so a
      // check that never ran for this question's type can't clear an
      // unrelated error slot. Rows live in their own sub-store, hence the
      // separate write.
      const { rows: patchedRows, ...valueChanges } = patch;
      if (Object.keys(valueChanges).length > 0) patchValues(key, valueChanges);
      if (patchedRows !== undefined) rows.setRows(key, patchedRows);
    },
    [patchValues, rows]
  );

  const handlers = useCallback(
    (question: ActiveQuestion, options?: { inlineAddRow?: boolean }): QuestionFieldHandlers => {
      const key = question.key;
      const isRepeatingList = question.questionType === 'repeatingList';
      return {
        onToggleChoice: (value) => toggleChoice(question, value),
        onOtherInputChange: (text) =>
          patchValues(key, { otherInput: text, showRequiredError: false }),
        onDateChange: (text) => patchValues(key, { dateValue: text, dateError: null }),
        // Reflect a live blur check into this question's `dateError`: a
        // message on failure, or null to clear.
        onDateBlur: () =>
          patchValues(key, {
            dateError: checkDateAnswer(question, getValues(key).dateValue, t).error,
          }),
        onTextChange: (text) =>
          patchValues(key, {
            textValue: text,
            showRequiredError: false,
            numberError: null,
            phoneError: null,
          }),
        onNumberBlur: () =>
          patchValues(key, {
            numberError: checkNumberAnswer(question, getValues(key).textValue, t),
          }),
        onPhoneBlur: () =>
          patchValues(key, { phoneError: checkPhoneAnswer(getValues(key).textValue, t) }),
        onUpdateRowValue: (rowId, columnName, text) =>
          rows.updateRowValue(key, rowId, columnName, text),
        onValidateRowCell: (rowId, column) => rows.validateRowCell(key, rowId, column),
        onRemoveRow: (rowId) => rows.removeRow(key, rowId),
        ...(options?.inlineAddRow && isRepeatingList
          ? { onAddRowInline: () => rows.addRow(key, question.columns ?? []) }
          : {}),
      };
    },
    [getValues, patchValues, rows, t, toggleChoice]
  );

  const addRow = useCallback(
    (question: ActiveQuestion) => rows.addRow(question.key, question.columns ?? []),
    [rows]
  );

  return useMemo(
    () => ({
      fieldState,
      handlers,
      pendingAnswer,
      applyErrorPatch,
      seedRows: rows.seedRows,
      addRow,
    }),
    [fieldState, handlers, pendingAnswer, applyErrorPatch, rows.seedRows, addRow]
  );
}
